import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AllianceRole, AllianceSunView, PlanetShips, PlayerRace, ShipId } from '@kaeliux/shared';
import {
    SUN_CONTROL_CLAIM_POPULATION,
    SUN_CONTROL_CLAIM_WINDOW_HOURS,
    SUN_CONTROL_LEDGER_CAP,
    SUN_CONTROL_MAINTAIN_POPULATION,
    getShipDefinition,
} from '@kaeliux/shared';
import { calculateDockedPopulation, recallableGarrisonPopulation } from '@kaeliux/game-engine';
import { getAllianceSuns, recallSunGarrison } from '../services/sun.service';
import { useNow } from '../hooks/useNow';
import { formatCountdown, formatFullNumber } from '../utils/formatters';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { NumberStepper } from './ui/NumberStepper';
import { Panel } from './ui/Panel';
import { ProgressBar } from './ui/ProgressBar';
import { linkButtonClasses } from './ui/buttonStyles';

function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
}

function until(date: Date | undefined, now: number): string {
    return formatCountdown(Math.max(0, (date?.getTime() ?? now) - now));
}

const RACE_LABELS: Record<PlayerRace, string> = { humans: 'Humans', robots: 'Robots', axons: 'Axons' };

function GarrisonRecallForm({ sun, onDone }: { sun: AllianceSunView; onDone: () => Promise<void> }) {
    const lanes = (Object.entries(sun.garrison) as [PlayerRace, PlanetShips | undefined][]).filter(([, ships]) => !!ships);
    const [race, setRace] = useState<PlayerRace>(lanes[0]?.[0] ?? 'humans');
    const [counts, setCounts] = useState<Partial<Record<ShipId, number>>>({});
    const [error, setError] = useState<string | null>(null);
    const lane = sun.garrison[race] ?? {};
    const selectedPopulation = calculateDockedPopulation(race, counts);
    const recallable = recallableGarrisonPopulation({ control: sun.control, garrison: sun.garrison });

    const mutation = useMutation({
        mutationFn: () => recallSunGarrison({ galaxy: sun.galaxy, system: sun.system, race, ships: counts }),
        onSuccess: async () => {
            setCounts({});
            setError(null);
            await onDone();
        },
        onError: (e) => setError(errorMessage(e, 'Failed to recall the garrison.')),
    });

    return (
        <div className="flex flex-col gap-2 border-t border-hairline pt-3" data-testid={`sun-recall-${sun.sunId}`}>
            <p className="text-label text-content-muted">
                Recall surplus to the Team planet — up to {formatFullNumber(recallable)} population; at least{' '}
                {formatFullNumber(SUN_CONTROL_MAINTAIN_POPULATION)} must stay.
            </p>
            {lanes.length > 1 && (
                <div className="flex gap-2 flex-wrap">
                    {lanes.map(([laneRace]) => (
                        <Button
                            key={laneRace}
                            type="button"
                            size="sm"
                            variant={laneRace === race ? 'primary' : 'secondary'}
                            onClick={() => { setRace(laneRace); setCounts({}); }}
                        >
                            {RACE_LABELS[laneRace]}
                        </Button>
                    ))}
                </div>
            )}
            <div className="flex flex-col gap-2">
                {(Object.entries(lane) as [string, number | undefined][]).filter(([, held]) => (held ?? 0) > 0).map(([key, held]) => {
                    const shipId = Number(key) as ShipId;
                    return (
                        <div key={key} className="flex items-center justify-between gap-3">
                            <span className="text-body text-content-secondary">
                                {getShipDefinition(race, shipId).name} <span className="text-content-muted">({formatFullNumber(held ?? 0)} held)</span>
                            </span>
                            <NumberStepper
                                label={`Recall ${getShipDefinition(race, shipId).name}`}
                                value={counts[shipId] ?? 0}
                                min={0}
                                max={held ?? 0}
                                onChange={(value) => setCounts((c) => ({ ...c, [shipId]: value }))}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
                <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    loading={mutation.isPending}
                    disabled={selectedPopulation === 0}
                    onClick={() => mutation.mutate()}
                >
                    Recall {formatFullNumber(selectedPopulation)} population
                </Button>
                {error && <p className="text-label text-status-danger" role="alert">{error}</p>}
            </div>
        </div>
    );
}

function SunCard({ sun, isLeader, now, onChanged }: { sun: AllianceSunView; isLeader: boolean; now: number; onChanged: () => Promise<void> }) {
    const { control } = sun;
    const coords = `${sun.galaxy}:${sun.system}`;
    const immune = control.reclaimImmuneUntil && control.reclaimImmuneUntil.getTime() > now;
    return (
        <Panel variant="solid" padding="md" className="flex flex-col gap-3" data-testid={`alliance-sun-${sun.sunId}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Link to={`/galaxy/${sun.galaxy}/${sun.system}`} className="text-title text-content-primary hover:underline">
                        Sun {coords}
                    </Link>
                    <Badge tone={control.status === 'controlled' ? 'success' : 'warning'}>
                        {control.status === 'controlled' ? 'Controlled' : 'Claimed'}
                    </Badge>
                    {control.stronghold && <Badge tone="accent">Stronghold</Badge>}
                    {sun.underAttack && <Badge tone="danger">Under attack</Badge>}
                </div>
                {isLeader && (
                    <Link to={`/team-planet/fleets?mission=sunGarrison&target=${coords}:0`} className={linkButtonClasses('secondary', 'sm')}>
                        Send garrison
                    </Link>
                )}
            </div>

            <div className="flex flex-col gap-1">
                <p className="text-body text-content-secondary" data-testid="alliance-sun-garrison">
                    Garrison: {formatFullNumber(sun.garrisonPopulation)} / {formatFullNumber(SUN_CONTROL_LEDGER_CAP)} population
                </p>
                <ProgressBar value={sun.garrisonPopulation / SUN_CONTROL_LEDGER_CAP} />
            </div>

            <ul className="flex flex-col gap-1 text-label text-content-muted">
                {control.status === 'claimed' && (
                    <li className="text-status-warning" data-testid="alliance-sun-claim-deadline">
                        Land {formatFullNumber(Math.max(0, SUN_CONTROL_CLAIM_POPULATION - sun.garrisonPopulation))} more population
                        within {until(control.claimExpiresAt, now)} to take control, or the claim lapses.
                    </li>
                )}
                {control.topUpExpiresAt && (
                    <li className="text-status-danger" data-testid="alliance-sun-top-up-deadline">
                        Restore the garrison to {formatFullNumber(SUN_CONTROL_MAINTAIN_POPULATION)} population within {until(control.topUpExpiresAt, now)}, or the Sun is lost.
                    </li>
                )}
                {control.stronghold && <li>Held off a reclaim — this Sun can never be attacked again.</li>}
                {!control.stronghold && immune && <li>Immune to reclaims for {until(control.reclaimImmuneUntil, now)}.</li>}
                {sun.underAttack && (
                    <li className="text-status-danger">
                        A reclaim is inbound{sun.attackArrivesAt ? ` — it arrives in ${until(sun.attackArrivesAt, now)}` : ''}.
                    </li>
                )}
            </ul>

            {isLeader && control.status === 'controlled' && recallableGarrisonPopulation({ control, garrison: sun.garrison }) > 0 && (
                <GarrisonRecallForm sun={sun} onDone={onChanged} />
            )}
        </Panel>
    );
}

/**
 * The alliance's Suns: every one it has claimed or controls, with its
 * garrison, its deadlines and — for the leader — the way to top it up or
 * bring its surplus home. Any member may look.
 */
export default function AllianceSunsTab({ viewerRole }: { viewerRole: AllianceRole | null }) {
    const now = useNow();
    const queryClient = useQueryClient();
    const sunsQuery = useQuery({ queryKey: ['alliance', 'suns'], queryFn: getAllianceSuns, refetchInterval: 30_000 });
    const isLeader = viewerRole === 'leader';
    const refresh = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['alliance', 'suns'] }),
            queryClient.invalidateQueries({ queryKey: ['teamPlanet'] }),
        ]);
    };

    if (sunsQuery.isPending) {
        return <Panel variant="solid" padding="md"><p className="text-content-muted animate-pulse">Loading Suns…</p></Panel>;
    }
    if (sunsQuery.isError) {
        return <Panel variant="solid" padding="md"><p className="text-status-danger">{errorMessage(sunsQuery.error, 'Failed to load the Suns.')}</p></Panel>;
    }
    const suns = sunsQuery.data ?? [];
    if (suns.length === 0) {
        return (
            <Panel variant="solid" padding="md" data-testid="alliance-suns-empty">
                <p className="text-content-secondary">Your alliance holds no Suns yet.</p>
                <p className="text-label text-content-muted mt-1">
                    Win a Sun battle from the Team planet to claim one, then land {formatFullNumber(SUN_CONTROL_CLAIM_POPULATION)} population
                    of garrison there within {SUN_CONTROL_CLAIM_WINDOW_HOURS} hours to control it.
                </p>
            </Panel>
        );
    }
    return (
        <div className="flex flex-col gap-3" data-testid="alliance-suns">
            {suns.map((sun) => <SunCard key={sun.sunId} sun={sun} isLeader={isLeader} now={now} onChanged={refresh} />)}
        </div>
    );
}
